'use client'

import { generateClient } from 'aws-amplify/api'
import type { GraphQLResult } from 'aws-amplify/api'
import { fetchAuthSession } from 'aws-amplify/auth'

import { cognitoMonitoring } from '@/services/auth/cognitoMonitoring.client'

import { getClientGraphqlAuthOptions } from '@/services/amplify/authMode'
import { ensureClientAmplifyConfigured } from '@/services/amplify/configureClientAmplify'

export type AmplifyGraphQLClient = {
  graphql: <T>(...args: unknown[]) => Promise<GraphQLResult<T>>
}

let cachedClient: AmplifyGraphQLClient | null = null

export function getAmplifyClient(): AmplifyGraphQLClient {
  if (!cachedClient) {
    ensureClientAmplifyConfigured()
    const lambdaAuthOptions = getClientGraphqlAuthOptions(async () => {
      const session = await cognitoMonitoring.observe(
        'session_check',
        'graphql_auth',
        () => fetchAuthSession(),
      )
      return session.tokens?.idToken?.toString()
    })

    const generatedClient = (lambdaAuthOptions
      ? generateClient(lambdaAuthOptions)
      : generateClient()) as unknown as AmplifyGraphQLClient

    cachedClient = {
      graphql: <T>(...args: unknown[]): Promise<GraphQLResult<T>> => {
        const options = args[0]
        const query =
          options && typeof options === 'object' && 'query' in options
            ? options.query
            : undefined
        const isMutation =
          typeof query === 'string' && /^\s*mutation\b/.test(query)

        // Amplify returns an observable synchronously for subscriptions.
        // Data remains the admission authority for protected mutations.
        if (!isMutation) return generatedClient.graphql<T>(...args)

        return (async () => {
          const result = await generatedClient.graphql<T>(...args)
          if (result.errors?.length) {
            throw Object.assign(
              new Error(
                result.errors.map((error) => error.message).join('; ') ||
                  'GraphQL mutation failed',
              ),
              { errors: result.errors },
            )
          }
          return result
        })()
      },
    }
  }
  return cachedClient
}
